import { z } from "zod";
import { DraftQuestion } from "./article.js";

/**
 * The site feed, v1 ([21 §8](../../../docs/21-articles-and-seo.md#8--the-feed-contract-for-the-sites-developers)).
 *
 * This is a contract with somebody else's developers, so it is written down here and held to the example by a test.
 * Joogoos serves a site's published editions as JSON with Markdown bodies; the site fetches it when it builds. We
 * hold no key to the customer's code, repository or host, and they hold nothing of ours but a read key.
 *
 * A change that is not additive is `/feed/v2/`, served beside v1 until the site moves.
 */

export const FEED_VERSION = 1;
/** `jgf_` and 43 characters of base64url, shown once and kept as a SHA-256 hash. */
export const FEED_KEY_PREFIX = "jgf_";
/** Sixty reads an hour per key is a build every minute, which is more than any site builds. */
export const FEED_READS_AN_HOUR = 60;

export const FeedAlternate = z.object({
  hreflang: z.string().regex(/^([a-z]{2}|x-default)$/),
  url: z.url(),
});
export type FeedAlternate = z.infer<typeof FeedAlternate>;

export const FeedEntry = z.object({
  /** The edition: one article in one language. */
  id: z.uuid(),
  /** Shared by every language of the article, and the `translationKey` a content loader groups by. */
  articleId: z.uuid(),
  lang: z.string().regex(/^[a-z]{2}$/),
  slug: z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/).max(60),
  /** prefix + blogPath + / + slug, as the site serves it. */
  path: z.string(),
  url: z.url(),
  title: z.string().max(60),
  description: z.string().max(155),
  publishedAt: z.iso.datetime(),
  modifiedAt: z.iso.datetime(),
  /** Every published language of this article, this one included, then `x-default`. An unpublished one is never here. */
  alternates: z.array(FeedAlternate),
  /** The same fields YAML frontmatter would carry, for a content loader that wants files. */
  frontmatter: z.looseObject({
    title: z.string(), description: z.string(), slug: z.string(), lang: z.string(),
    publishedAt: z.string(), modifiedAt: z.string(), translationKey: z.string(),
  }),
  /** Markdown, H2 and below: no H1, no citations, and the FAQ is not in it. */
  body: z.string(),
  faq: z.array(DraftQuestion),
  /** Rendered as-is into one `<script type="application/ld+json">`. Built by code, never by a model. */
  jsonLd: z.array(z.record(z.string(), z.unknown())),
});
export type FeedEntry = z.infer<typeof FeedEntry>;

export const FeedSite = z.object({
  id: z.uuid(),
  /** No trailing slash. */
  baseUrl: z.url(),
  blogPath: z.string().regex(/^\/[a-z0-9-/]*[a-z0-9]$/),
  trailingSlash: z.boolean(),
  defaultLang: z.string().regex(/^[a-z]{2}$/),
  languages: z.array(z.object({ lang: z.string().regex(/^[a-z]{2}$/), prefix: z.string() })).min(1),
});
export type FeedSite = z.infer<typeof FeedSite>;

export const FeedV1 = z.object({
  version: z.literal(FEED_VERSION),
  generatedAt: z.iso.datetime(),
  site: FeedSite,
  entries: z.array(FeedEntry),
  /** A slug changed after publishing: the old address is a 301 to the new one, for as long as the site serves it. */
  redirects: z.array(z.object({ from: z.string(), to: z.string() })),
  /** Withdrawn: a 410, or a 404. */
  gone: z.array(z.object({ path: z.string(), since: z.iso.datetime() })),
});
export type FeedV1 = z.infer<typeof FeedV1>;

/** Where a site fetches its feed. The key goes in the `Authorization` header only, never in the address. */
export const feedUrl = (appUrl: string, siteId: string) => `${appUrl.replace(/\/+$/, "")}/feed/v1/sites/${siteId}.json`;

/**
 * The alternates for one edition: every published language of the same article, then `x-default` on the default
 * language. A language that is not published is never linked, because a `hreflang` to a 404 is worse than no
 * `hreflang` at all.
 */
export function alternatesFor(
  published: { lang: string; url: string }[],
  defaultLang: string,
): FeedAlternate[] {
  const out: FeedAlternate[] = published.map((p) => ({ hreflang: p.lang, url: p.url }));
  const fallback = published.find((p) => p.lang === defaultLang);
  if (fallback) out.push({ hreflang: "x-default", url: fallback.url });
  return out;
}
